import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster } from 'sonner';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Settings,
  LogOut,
  Menu,
  Home,
  ChevronRight,
  MapPin,
  Phone,
  Globe,
  Tag,
  Stethoscope,
  Hotel,
  Car,
  Palmtree,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Component
const SidebarNav = ({ isOpen, setIsOpen }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  const navItems = [
    { icon: LayoutDashboard, label: 'Overview', path: '/dashboard' },
    { icon: Sparkles, label: 'AI Estimate', path: '/dashboard/ai-estimate', isNew: true },
    { icon: FileText, label: 'Cost Estimates', path: '/dashboard/estimates' },
    { icon: Users, label: 'Referrals', path: '/dashboard/referrals' },
    { icon: Building2, label: 'Business Network', path: '/dashboard/businesses' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-100 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <div>
                <span className="font-semibold text-primary text-sm">MED CONNECT</span>
                <span className="text-accent font-light ml-1 text-sm">ALBANIA</span>
              </div>
            </Link>
          </div>
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <span className="text-accent font-semibold">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Menu</p>
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive(item.path) ? 'bg-accent/10 text-accent' : 'text-muted-foreground hover:bg-slate-50 hover:text-primary'}`}>
                <item.icon className="w-5 h-5" />{item.label}
                {item.isNew && <Badge className="ml-auto bg-accent text-white text-xs">NEW</Badge>}
              </Link>
            ))}
            {isAdmin && (
              <div className="pt-4 mt-4 border-t border-slate-100">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Admin</p>
                <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary">
                  <Settings className="w-5 h-5" />Admin Panel
                </Link>
              </div>
            )}
          </nav>
          <div className="p-4 border-t border-slate-100 space-y-2">
            <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary">
              <Home className="w-5 h-5" />Back to Home
            </Link>
            <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full">
              <LogOut className="w-5 h-5" />Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Business Card Component
const BusinessCard = ({ business }) => {
  const getCategoryIcon = (category) => {
    const icons = {
      clinic: Stethoscope,
      hotel: Hotel,
      transport: Car,
      tourism: Palmtree
    };
    const Icon = icons[category] || Building2;
    return <Icon className="w-5 h-5" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      clinic: 'bg-blue-100 text-blue-700',
      hotel: 'bg-purple-100 text-purple-700',
      transport: 'bg-orange-100 text-orange-700',
      tourism: 'bg-green-100 text-green-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getCategoryColor(business.category)}`}>
            {getCategoryIcon(business.category)}
          </div>
          <div>
            <h3 className="font-semibold text-primary">{business.name}</h3>
            <Badge variant="secondary" className="capitalize">{business.category}</Badge>
          </div>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{business.description}</p>

      {business.discount && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 rounded-lg">
          <Tag className="w-4 h-4 text-green-600" />
          <span className="text-sm text-green-700 font-medium">{business.discount}</span>
        </div>
      )}

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="w-4 h-4" />
          <span>{business.address}</span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Phone className="w-4 h-4" />
          <span>{business.contact_info}</span>
        </div>
        {business.website && (
          <div className="flex items-center gap-2 text-accent">
            <Globe className="w-4 h-4" />
            <a href={business.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
              Visit Website
            </a>
          </div>
        )}
      </div>

      {business.offerings?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Services</p>
          <div className="flex flex-wrap gap-2">
            {business.offerings.slice(0, 4).map((offering, index) => (
              <span key={index} className="px-2 py-1 bg-slate-100 rounded-md text-xs">
                {offering}
              </span>
            ))}
            {business.offerings.length > 4 && (
              <span className="px-2 py-1 bg-slate-100 rounded-md text-xs">
                +{business.offerings.length - 4} more
              </span>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Business Network Content
const BusinessNetworkContent = () => {
  const [businesses, setBusinesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await fetch(`${API_URL}/api/businesses`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setBusinesses(data);
        }
      } catch (error) {
        console.error('Failed to fetch businesses:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBusinesses();
  }, []);

  const categories = [
    { value: 'all', label: 'All Partners' },
    { value: 'clinic', label: 'Clinics' },
    { value: 'hotel', label: 'Hotels' },
    { value: 'transport', label: 'Transport' },
    { value: 'tourism', label: 'Tourism' },
  ];

  const filteredBusinesses = activeCategory === 'all' 
    ? businesses 
    : businesses.filter(b => b.category === activeCategory);

  return (
    <div className="space-y-6" data-testid="businesses-page">
      <div>
        <h1 className="text-2xl font-semibold text-primary">Business Network</h1>
        <p className="text-muted-foreground">Explore our partner network of clinics, hotels, and services.</p>
      </div>

      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="bg-slate-100 p-1">
          {categories.map((cat) => (
            <TabsTrigger key={cat.value} value={cat.value} className="data-[state=active]:bg-white">
              {cat.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredBusinesses.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="text-center py-12">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No businesses found</h3>
            <p className="text-muted-foreground">No partner businesses in this category yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBusinesses.map((business, index) => (
            <motion.div
              key={business.business_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <BusinessCard business={business} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Business Network Page
export default function BusinessNetworkPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" richColors />
      <SidebarNav isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100 lg:pl-64">
        <div className="flex items-center justify-between px-4 md:px-8 h-16">
          <div className="flex items-center gap-4">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2 text-sm">
              <Link to="/dashboard" className="text-muted-foreground hover:text-primary">Dashboard</Link>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">Business Network</span>
            </div>
          </div>
        </div>
      </header>
      
      <main className="lg:pl-64 pt-6 pb-12 px-4 md:px-8">
        <BusinessNetworkContent />
      </main>
    </div>
  );
}
